'use client';

import React from 'react';
import clsx from 'clsx';
import type { Consultation, MultistepFormProps } from './types';
import { StepsIndicator } from '@/components/step-indicator/StepsIndicator';
import { Checklist } from '@/components/checklist/Checklist';
import { Headline } from '@/components/headline/Headline';
import { useMultistepFormState } from './hooks/useMultistepFormState';
import StepRenderer from '@/sections/forms/multistep-form/steps/StepRenderer';
import Footer from '@/sections/forms/multistep-form/steps/Footer';
import { useTranslations } from '@/lib/locale-context';

const normalizeHiddenFields = (
  items?: Array<{ key?: string | null; name?: string | null; value?: unknown }>,
) =>
  (items ?? [])
    .map((item) => ({
      key: String(item?.key ?? item?.name ?? '').trim(),
      value: String(item?.value ?? ''),
    }))
    .filter((item) => item.key);

const resolvePresetConsultation = (
  consultation: MultistepFormProps['consultation'],
): Consultation | null => {
  if (!consultation) return null;
  if ('data' in consultation) return consultation.data ?? null;
  if ('title' in consultation) return consultation;
  return null;
};

export const MultistepForm: React.FC<MultistepFormProps> = ({
  mainTitle,
  headline,
  checklist,
  formHeadline,
  form,
  hiddenFields,
  consultation,
  topics,
  locale,
  successModalTitle,
  successModalDescription,
  variant,
  sectionId,
}) => {
  const { t } = useTranslations();
  const presetConsultation = React.useMemo(
    () => resolvePresetConsultation(consultation),
    [consultation],
  );
  const hasPresetConsultation = Boolean(presetConsultation);

  const formState = useMultistepFormState({
    presetConsultation,
    presetTopics: topics ?? '',
    locale: locale ?? 'de',
  });
  const resolvedHiddenFields = React.useMemo(() => {
    const merged = [
      ...normalizeHiddenFields(hiddenFields),
      ...normalizeHiddenFields(form?.hiddenFields),
    ];

    return merged.length ? merged : undefined;
  }, [hiddenFields, form?.hiddenFields]);
  const isDarkVariant = variant === 'dark';
  const isFinalState =
    formState.isSuccess || formState.isError || formState.isFinalStepSubmitting;
  const showFooter = formState.showFooter && !isFinalState;

  return (
    <div
      id={sectionId}
      className={clsx(
        'relative',
        isDarkVariant
          ? 'xl:bg-brand-700'
          : 'xl:bg-[linear-gradient(to_right,#07314c_0%,#07314c_50%,#ffffff_50%,#ffffff_100%)]',
      )}
    >
      <div className="xl:container xl:mx-auto">
        <section
          className={clsx(
            'relative grid grid-cols-1',
            !isFinalState && 'xl:grid-cols-2',
            'min-h-[100dvh] grid-rows-[auto_minmax(0,1fr)] xl:min-h-[800px] xl:grid-rows-none',
          )}
        >
          {/* ───────────── LEFT SIDE ───────────── */}
          <div
            className={clsx(
              'flex w-full items-center bg-brand-700 px-4 py-10 text-white sm:px-6 lg:px-8 lg:py-14 xl:px-0',
              isFinalState ? 'hidden' : 'flex',
            )}
          >
            <div className="flex flex-col gap-10">
              {!hasPresetConsultation && (
                <div className="hidden xl:block">
                  <StepsIndicator
                    totalSteps={formState.totalSteps}
                    currentStep={formState.stepIndicatorCurrent}
                    variant="light"
                    type="progress"
                    showLabel
                    renderLabel={({ current, total }) => (
                      <span className="text-sm text-white/80">
                        {t('progressiveForm.step', { current, total })}
                      </span>
                    )}
                  />
                </div>
              )}

              {mainTitle && (
                <p className="text-sm font-medium text-white/70">{mainTitle}</p>
              )}

              <div className="w-full xl:w-2/3">
                {headline ? <Headline {...headline} /> : null}
              </div>

              {checklist ? (
                <div className="mt-2 hidden xl:grid">
                  <Checklist {...checklist} />
                </div>
              ) : null}
            </div>
          </div>

          {/* ───────────── RIGHT SIDE ───────────── */}
          <div
            className={clsx(
              'flex min-h-0 flex-col xl:min-h-[800px]',
              isFinalState && 'xl:min-h-[100dvh]',
              isDarkVariant && 'xl:my-12',
            )}
          >
            {/* scrollable content */}
            <div
              className={clsx(
                'flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white px-4 pt-6 sm:px-6 lg:px-8 xl:overflow-y-auto xl:px-0 xl:pl-12 xl:pr-1',
                formState.currentStep === formState.totalSteps && !isFinalState
                  ? 'xl:pt-8'
                  : 'xl:pt-4',
                isFinalState && 'xl:px-0',
                isDarkVariant && 'xl:rounded-xl xl:pr-12',
                showFooter ? 'xl:pb-32' : 'xl:pb-20',
              )}
            >
              <div
                className={clsx(
                  'mx-auto flex w-full flex-1 flex-col',
                  !isFinalState && 'xl:max-w-[760px]',
                  isFinalState && 'xl:max-w-[640px]',
                  isFinalState
                    ? 'justify-center'
                    : formState.currentStep === formState.totalSteps
                      ? 'justify-start'
                      : 'justify-start xl:justify-center',
                )}
              >
                <div className="flex min-h-0 w-full flex-1 flex-col justify-center xl:min-h-[560px] xl:flex-none">
                  <div
                    className={clsx(
                      'min-h-0',
                      showFooter &&
                        'flex-1 overflow-y-auto pb-4 xl:flex-none xl:overflow-visible xl:pb-0',
                    )}
                  >
                    <StepRenderer
                      formState={formState}
                      formHeadline={formHeadline}
                      form={form}
                      hiddenFields={resolvedHiddenFields}
                      successModalTitle={successModalTitle}
                      successModalDescription={successModalDescription}
                    />
                  </div>
                  {/* FOOTER - full width with border, content in container */}
                  {showFooter && (
                    <div className="mt-auto shrink-0 xl:mt-0 xl:w-1/2">
                      <div className="xl:pr-0">
                        <Footer formState={formState} variant={variant} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MultistepForm;
